/** Natural "bend a straight segment" geometry (issue #540).
 *
 *  When the user grabs the middle of a straight construction segment
 *  and drags, the old behaviour set both bracketing bezier handles to
 *  the drag vector — so the tangents at the two endpoints jumped
 *  PERPENDICULAR to the segment, reading as an unusually sharp kink at
 *  the ends (and at any junction the segment shares with other lines).
 *
 *  Instead, treat the gesture as "fit a circular arc through the two
 *  endpoints and the dragged point" and approximate that arc with a
 *  single cubic. The endpoint tangents then angle ALONG the chord for a
 *  gentle drag (a shallow arc) and only approach perpendicular as the
 *  drag deepens toward a full semicircle — the standard, natural feel.
 *
 *  Pure math (no paper.js) so it stays unit-testable. */

export interface Vec2 {
  x: number;
  y: number;
}

/** Bezier handles that bend the straight segment `A`→`B` into the
 *  circular arc through `M` (the dragged point), as a SINGLE cubic.
 *  Returns the handles RELATIVE to their anchors: `out` is `A`'s
 *  `handleOut`, `in` is `B`'s `handleIn`. Returns null when `A`, `M`,
 *  `B` are collinear or degenerate (no arc — the caller keeps the
 *  segment straight). */
export function computeStraightBendHandles(
  A: Vec2,
  B: Vec2,
  M: Vec2,
): { out: Vec2; in: Vec2 } | null {
  // Circumcircle centre of the triangle (A, M, B).
  const ax = A.x,
    ay = A.y,
    bx = M.x,
    by = M.y,
    cx = B.x,
    cy = B.y;
  const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
  if (Math.abs(d) < 1e-9) return null; // collinear → no bend
  const a2 = ax * ax + ay * ay;
  const b2 = bx * bx + by * by;
  const c2 = cx * cx + cy * cy;
  const ox = (a2 * (by - cy) + b2 * (cy - ay) + c2 * (ay - by)) / d;
  const oy = (a2 * (cx - bx) + b2 * (ax - cx) + c2 * (bx - ax)) / d;
  const R = Math.hypot(A.x - ox, A.y - oy);
  if (R < 1e-9) return null;

  const vAx = A.x - ox;
  const vAy = A.y - oy;
  const vBx = B.x - ox;
  const vBy = B.y - oy;
  const vMx = M.x - ox;
  const vMy = M.y - oy;
  const angA = Math.atan2(vAy, vAx);
  const angB = Math.atan2(vBy, vBx);
  const angM = Math.atan2(vMy, vMx);
  const TWO_PI = Math.PI * 2;
  const mod = (x: number) => ((x % TWO_PI) + TWO_PI) % TWO_PI;
  // Central angle of the arc from A to B that PASSES THROUGH M. If, going
  // counter-clockwise from A, we reach M before B, the through-M arc is
  // the CCW one (sweep = dB); otherwise it's the CW one (2π − dB).
  const dM = mod(angM - angA);
  const dB = mod(angB - angA);
  let theta: number;
  let ccw: boolean;
  if (dM <= dB) {
    theta = dB;
    ccw = true;
  } else {
    theta = TWO_PI - dB;
    ccw = false;
  }
  if (theta < 1e-6) return null;

  // Single-cubic approximation of an arc of angle θ: handle length
  // = (4/3)·tan(θ/4)·R along the endpoint tangents. tan(θ/4) grows from
  // ~θ/4 (shallow → handles nearly along the chord) to 1 at θ=π
  // (semicircle → handles perpendicular to the chord).
  const mag = (4 / 3) * Math.tan(theta / 4) * R;

  // Travel-direction tangent (unit) = radial rotated 90° in the sweep
  // sense. CCW: (x,y)→(−y,x); CW: (x,y)→(y,−x).
  const rot = (x: number, y: number) =>
    ccw ? { x: -y, y: x } : { x: y, y: -x };
  const tA = rot(vAx / R, vAy / R); // leaving A along the arc
  const tB = rot(vBx / R, vBy / R); // leaving B along the arc
  return {
    out: { x: tA.x * mag, y: tA.y * mag }, // A's handleOut: forward
    in: { x: -tB.x * mag, y: -tB.y * mag }, // B's handleIn: backward
  };
}
